import React from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

// Components
import CustomTypePills from '@/views/components/flaggableInstruments/CustomTypePills';
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';
import MaskedData from '@/views/components/common/MaskedData';
import TableMenu from '@/views/components/data/DataTableV2/TableMenu';
import { LoadingSpinner } from '@/views/components/common';
import { OwnProps as PortfolioLinePerformanceProps } from '@/views/components/misc/PortfolioLinePerformance';
import { PillLabel, PortfolioLinePerformance, ContactLabel } from '@/views/components/misc';
import { PrimaryButton } from '@/views/components/buttons';
import { Tooltip } from '@/views/components/tooltips';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS, PORTFOLIO_TABLE_COLUMNS_KEYS } from '@/enums/Portfolio';

// Libraries
import { CellContext, ColumnDef, ColumnSort, Row } from '@tanstack/react-table';
import classNames from 'classnames';
import moment from 'moment';

// Redux
import store from '@/redux/store';
import { ACCEPT_PORTFOLIO, removePlanAccountFromPortfolio } from '@/redux/modules/contacts';
import { selectIsLoadingParamsSome, selectIsLoadingSome } from '@/redux/modules/ui/loaders';
import { ReduxState } from '@/redux/modules/types';
import { GET_PORTFOLIO_BY_ID } from '@/redux/modules/portfolios';

// Types
import Enterprise from '@/types/enterprise';
import FlaggableInstrument from '@/types/flaggable-instrument';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import User from '@/types/user';
import { MultiselectAction } from '@/types/data-table';
import GenericContact from '@/types/generic-contact';

// Utils
import { FormattedMessage, formatLocalizedString, formatMessage } from '@/utils/i18n';
import { decodeIdForExistingClientPortfolio, getPortfolioType, hasEmptyHoldings } from '@/utils/portfolios';
import { isEnterpriseModel } from '@/utils/portfolio/utils';
import { portfolioToComparisonItem } from '@/utils/comparison';
import { prepareMenuItems } from '@/utils/data-table';
import { ADD_PORTFOLIOS_TO_COMPARISON } from '@/redux/modules/comparison';
import { canViewPlanning } from '@/utils/portfolio/portfolio';
import { PortfolioFlag } from '@/utils/constants';
import { Account } from '@/types/planning';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

export enum WarningIcon {
  LOCK,
  ERROR,
}

export enum PortfolioAction {
  DELETE_SINGLE = 'deleteSingle',
  EDIT_SINGLE = 'editSingle',
  CLONE_SINGLE = 'cloneSingle',
  ACCEPT_SINGLE = 'acceptSingle',
  REMOVE_PLAN_ACCOUNT = 'removePlanAccount',
  COPY_SINGLE = 'copySingle',
}

export interface WarningProps {
  message: string;
  title: string;
  icon: WarningIcon;
}

type DispatchProps<T> = {
  removePlanAccountFromPortfolio: (portfolio: T) => Promise<Portfolio>;
};

type StateProps = {
  evaluatingPortfolioForComparison?: boolean;
  user: Nullable<User>;
};

type ComponentProps<T> = {
  portfolios?: T[];
  displayedColumns: PORTFOLIO_TABLE_COLUMNS[];
  displayedMetrics: PORTFOLIO_METRICS_FIELDS[];
  className?: string;
  serverSideSorting?: boolean;
  warningDetails?: WarningProps;
  locked?: boolean;
  comparingPortfolioIds?: string[];
  highlightedIds?: string[];
  showLoadMore?: boolean;
  onLoadMore?: () => void;
  onDelete?: (portfolios: T[]) => void;
  onEdit?: (portfolio: T) => void;
  onCompare?: (portfolios: T[]) => void;
  onAccept?: (portfolio: T) => void;
  onCopy?: (portfolio: T) => void;
  selectedTitle: string;
  messageIds?: {
    compareMultiple?: string;
    deleteMultiple?: string;
    deleteSingle?: string;
    editSingle?: string;
    copySingle?: string;
    acceptSingle?: string;
  };
  missingProductPredicate?: (portfolio: T) => boolean;
  canEditPredicate?: (portfolio: T) => boolean;
  canDeletePredicate?: (portfolio: T) => boolean;
  canClonePredicate?: (portfolio: T) => boolean;
  canAcceptPredicate?: (portfolio: T) => boolean;
  performanceSubcomponent?: (row: any) => React.JSX.Element;
  sortedColumns?: ColumnSort[];
  isThemeLight?: boolean;
  query?: string;
  hasMore?: boolean;
  onServerSideSorting?: (sort: ColumnSort) => void;
  onRowSelectionChange?: (selectedRows: T[]) => void;
  forceSelectable?: boolean;
};

type Props<T> = ComponentProps<T> & DispatchProps<T> & StateProps;

type State<T> = {
  selectedPortfolios: T[];
  isComparingMultiplePortfolios: boolean;
  columns: ColumnDef<T>[];
};

type MenuOption<T> = {
  key: PortfolioAction;
  value: string;
  onClick: ((portfolio: T) => void) | undefined;
  icon: string;
  isAllowed: (portfolio: T) => boolean;
  className?: string;
  loaderKeys?: string | string[];
};

interface BasePortfolio {
  _id?: string;
  enterprise?: Enterprise;
  enterpriseGroups?: string[];
  contact?: GenericContact;
  allocated?: boolean;
  planning?: { planId: string; planAccountId: string; planAccount?: Account };
  holdings?: PortfolioHolding[];
}

export class PortfoliosTableV2<T extends BasePortfolio & FlaggableInstrument> extends React.Component<Props<T>, State<T>> {
  canDeleteMultiple = true;

  constructor(props: Props<T>) {
    super(props);
    this.state = {
      selectedPortfolios: [],
      isComparingMultiplePortfolios: false,
      columns: this.getTableColumns(),
    };
  }

  readonly i18nBasePath = 'PortfoliosTable';

  componentDidUpdate(prevProps: Readonly<Props<T>>): void {
    const { evaluatingPortfolioForComparison, displayedColumns, locked } = this.props;

    if (prevProps.evaluatingPortfolioForComparison !== evaluatingPortfolioForComparison && !evaluatingPortfolioForComparison) {
      this.setState({ isComparingMultiplePortfolios: false });
    }

    if (!isEqual(prevProps.displayedColumns, displayedColumns) || prevProps.locked !== locked) {
      this.setState({ columns: this.getTableColumns() });
    }
  }

  isComparingIndividualPortfolio = (portfolio?: T) => {
    const { comparingPortfolioIds, evaluatingPortfolioForComparison, missingProductPredicate } = this.props;

    // If portfolio has missing product, we want to still show the loader on the compare button although we are not adding it to the comparison directly
    if (portfolio && missingProductPredicate && missingProductPredicate(portfolio)) {
      return evaluatingPortfolioForComparison;
    }

    // Check if this specific portfolio is currently being compared
    // This is so the loader shows only on the portfolio that is being compared
    const portfolioId = portfolio?._id;
    const hasValidPortfolioId = !!portfolioId;
    const hasComparingIds = !!comparingPortfolioIds;
    const isPortfolioInComparisonList = hasValidPortfolioId && hasComparingIds && comparingPortfolioIds.includes(portfolioId);

    return evaluatingPortfolioForComparison && isPortfolioInComparisonList;
  };

  /**
   * Verifies if the menu options should be in a loading state.
   * This is inferred from the menu options definition using the "loaderKey" property.
   */
  shouldShowMenuLoader = (portfolio: T, menuOptions: MenuOption<T>[]) => {
    const keys = menuOptions.flatMap(option => option.loaderKeys ?? []);
    return selectIsLoadingParamsSome(store.getState(), keys).some(p => p?._id === portfolio._id || p.id === portfolio._id);
  };

  /**
   * Returns additional CSS class names for highlighted rows.
   */
  getRowClassNames = (row: Row<T>): string => {
    const { highlightedIds } = this.props;
    const portfolioId = row.original._id;

    if (highlightedIds && portfolioId && highlightedIds.includes(portfolioId)) {
      return 'highlighted-row';
    }

    return '';
  };

  getUnavailableActions = (portfolio: T) =>
    [
      {
        key: PortfolioAction.CLONE_SINGLE,
        value: 'Copied',
        hidden: this.props.canClonePredicate && !this.props.canClonePredicate(portfolio),
      },
      {
        key: PortfolioAction.EDIT_SINGLE,
        value: 'Edited',
        hidden: this.props.canEditPredicate && !this.props.canEditPredicate(portfolio),
      },
    ].filter(option => option.hidden);

  onStartComparison = (selectedPortfolios: T[]) => {
    if (this.props.onCompare) {
      this.setState({ isComparingMultiplePortfolios: true });
      this.props.onCompare(selectedPortfolios);
    }
  };

  renderNameCell = (info: CellContext<T, any>) => {
    const data = info.row.original;
    const value = info.getValue();

    return (
      <div className="name-cell">
        <Tooltip className="custom-tooltip-v2" message={typeof value === 'string' ? value : formatLocalizedString(value)} data-private data-dd-privacy="mask">
          <span id={`portfolio-name-${data._id}`} className="portfolio-name">
            {typeof value === 'string' ? value : formatLocalizedString(value)}
          </span>
        </Tooltip>
        <CustomTypePills className="custom-type-pill" flaggableInstrument={data} />
      </div>
    );
  };

  renderContactsCell = (info: CellContext<T, any>) => {
    const { contact } = info.row.original;

    if (!contact) {
      return <>-</>;
    }

    return <ContactLabel contact={contact} showNameLabel={false} showTooltip />;
  };

  renderGroupCell = (info: CellContext<T, any>) => {
    const value = info.getValue();
    const { user } = this.props;
    if (!value || value.length === 0) {
      return (
        <Tooltip className="custom-tooltip-v2" message={formatMessage('PortfoliosTable.Data.MyModel')} data-private data-dd-privacy="mask">
          <span>{formatMessage('PortfoliosTable.Data.MyModel')}</span>
        </Tooltip>
      );
    }

    const enterpriseModelName = user?.enterprise?.name
      ? formatMessage('PortfoliosTable.Data.EnterpriseModelName', { name: user.enterprise.name })
      : formatMessage('PortfoliosTable.Data.EnterpriseModel');

    return (
      <Tooltip className="custom-tooltip-v2" message={enterpriseModelName} data-private data-dd-privacy="mask">
        <span>{enterpriseModelName}</span>
      </Tooltip>
    );
  };

  renderPlanAccountCell = (info: CellContext<T, any>) => {
    const planning = info.getValue();
    if (!planning?.planAccount?.name) {
      return <></>;
    }
    return <span>{planning.planAccount.name}</span>;
  };

  renderRiskProfileCell = (info: CellContext<T, any>) => {
    const riskProfile = info.getValue();
    if (!riskProfile) {
      return <></>;
    }
    return (
      <Tooltip className="custom-tooltip-v2" message={formatLocalizedString(riskProfile.profile.title)}>
        <span>
          <PillLabel text={formatLocalizedString(riskProfile.profile.title)} />
        </span>
      </Tooltip>
    );
  };

  renderLastModifiedDataCell = (info: CellContext<T, any>) => {
    const date = info.getValue();
    if (!date) {
      return <></>;
    }

    const currentYear = new Date().getFullYear();
    const parsedDate = moment(date);

    const format = parsedDate.year() !== currentYear ? 'LastModifiedDateFormatWithYear' : 'LastModifiedDateFormat';
    const message = formatMessage(`PortfoliosTable.Data.${format}`);
    const parsedDateFormatted = parsedDate.format(message);
    return (
      <Tooltip className="custom-tooltip-v2" message={parsedDateFormatted}>
        <span className="grey-11">{parsedDateFormatted}</span>
      </Tooltip>
    );
  };

  getExpandedContent = (row: any) => {
    // If a subComponent was passed as prop render it
    if (this.props.performanceSubcomponent) {
      return this.props.performanceSubcomponent(row);
    }

    // Otherwise default to the PortfolioLinePerformance that fetches data for a regular portfolio
    const subComponentProps: PortfolioLinePerformanceProps<Portfolio> = {
      portfolio: row.original,
      transformPortfolioToComparisonItem: (appRegion, validateIncomeStream) => portfolioToComparisonItem(row.original, appRegion, validateIncomeStream),
      displayedMetrics: this.props.displayedMetrics,
      endpointEntity: 'portfolios',
      queryParams: {
        contactId: row.original.contact?._id,
        portfolioType: getPortfolioType(row.original),
      },
    };
    return <PortfolioLinePerformance className="portfolio-line-performance-v2" {...subComponentProps} />;
  };

  renderWarningCell = (info: CellContext<T, any>) => {
    const { missingProductPredicate } = this.props;
    const data = info.row.original;

    // Warning for unallocated portfolios
    if (this.isUnallocatedPortfolio(data)) {
      return (
        <Tooltip
          className="custom-tooltip-v2"
          children={<i className="icon-ds-warning" />}
          icon="icon-info"
          title={formatMessage('PortfoliosTable.Warnings.UnallocatedPortfolio.Title')}
          message={formatMessage('PortfoliosTable.Warnings.UnallocatedPortfolio.Message')}
        />
      );
    }

    // Warning for Missing Product
    if (missingProductPredicate && missingProductPredicate(data)) {
      return (
        <Tooltip
          icon="icon-info"
          children={<i className="icon-exclamation" />}
          className="custom-tooltip-v2"
          title={formatMessage('PortfoliosTable.Warnings.MissingProduct.Title')}
          message={formatMessage('PortfoliosTable.Warnings.MissingProduct.Message')}
        />
      );
    }

    return <></>;
  };

  renderLockCell = (info: CellContext<T, any>) => {
    const data = info.row.original;
    const unavailableActions = this.getUnavailableActions(data);
    let action = null;
    if (isEnterpriseModel(data)) {
      // Should be locked for enterprise models regardless of available actions because it should be edited from the admin section
      action = 'Edited';
    } else if (unavailableActions.length > 0) {
      action = unavailableActions.length === 2 ? 'EditedOrCopied' : unavailableActions[0].value;
    }
    return action ? (
      <Tooltip
        className="custom-tooltip-v2"
        icon="icon-info"
        title={formatMessage('PortfoliosTable.Warnings.Lock.Title')}
        message={formatMessage('PortfoliosTable.Warnings.Lock.Message', {
          action: formatMessage(`PortfoliosTable.Warnings.${action}`),
        })}
      >
        <i className="icon-lock" />
      </Tooltip>
    ) : (
      <></>
    );
  };

  getMultiselectActions = () => {
    const { displayedColumns, messageIds } = this.props;

    if (!displayedColumns.includes(PORTFOLIO_TABLE_COLUMNS.COMPARE)) {
      return [];
    }

    const compareAction: MultiselectAction<T> = {
      icon: 'ds-add',
      label: <FormattedMessage id={messageIds?.compareMultiple || 'PortfoliosTable.Buttons.AddToComparison'} />,
      loading: this.state.isComparingMultiplePortfolios,
      onClick: (selectedRows: T[]) => {
        this.onStartComparison(selectedRows);
      },
    };

    return [compareAction];
  };

  getMenuActions = () => {
    const {
      canDeletePredicate,
      canEditPredicate,
      canAcceptPredicate,
      canClonePredicate,
      displayedColumns,
      messageIds,
      onAccept,
      onEdit,
      onDelete,
      onCopy,
      removePlanAccountFromPortfolio,
      user,
    } = this.props;

    if (!displayedColumns.includes(PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE)) {
      return [];
    }

    return [
      {
        key: PortfolioAction.EDIT_SINGLE,
        value: formatMessage(messageIds?.editSingle || 'PortfoliosTable.Buttons.EditPortfolio'),
        onClick: (portfolio?: T) => {
          if (onEdit && portfolio) {
            return onEdit(portfolio);
          }
        },
        icon: 'icon-ds-edit',
        isAllowed: (portfolio: T): boolean => canEditPredicate?.(portfolio) ?? false,
      },
      {
        key: PortfolioAction.COPY_SINGLE,
        value: formatMessage(messageIds?.copySingle || 'PortfoliosTable.Buttons.CopyPortfolio'),
        onClick: (portfolio?: T) => {
          if (onCopy && portfolio) {
            return onCopy(portfolio);
          }
        },
        icon: 'icon-ds-copy',
        isAllowed: (portfolio: T): boolean => (onCopy && canClonePredicate?.(portfolio)) ?? false,
        loaderKeys: GET_PORTFOLIO_BY_ID.type,
      },
      {
        key: PortfolioAction.DELETE_SINGLE,
        icon: 'icon-ds-delete',
        className: 'destructive',
        value: formatMessage(messageIds?.deleteSingle || 'PortfoliosTable.Buttons.DeletePortfolio'),
        onClick: (portfolio?: T) => {
          if (onDelete && portfolio) {
            return onDelete([portfolio]);
          }
        },
        isAllowed: (portfolio: T) => canDeletePredicate?.(portfolio) ?? false,
      },
      {
        key: PortfolioAction.REMOVE_PLAN_ACCOUNT,
        icon: 'icon-ds-remove-circle-outline',
        className: 'delete-account destructive',
        value: formatMessage('PortfoliosTable.Buttons.RemovePlanAccount'),
        onClick: (portfolio?: T) => {
          if (removePlanAccountFromPortfolio && portfolio) {
            removePlanAccountFromPortfolio(portfolio);
          }
        },
        isAllowed: (portfolio: T): boolean => canViewPlanning(user, portfolio?.contact) && !!portfolio?.planning?.planAccountId,
      },
      {
        key: PortfolioAction.ACCEPT_SINGLE,
        value: formatMessage(messageIds?.acceptSingle || 'PortfoliosTable.Buttons.AcceptPortfolio'),
        onClick: (portfolio?: T) => {
          if (onAccept && portfolio) {
            return onAccept(portfolio);
          }
        },
        icon: 'icon-save-to',
        isAllowed: (portfolio: T): boolean => (!this.isUnallocatedPortfolio(portfolio) && canAcceptPredicate?.(portfolio)) ?? false,
        loaderKeys: ACCEPT_PORTFOLIO.type,
      },
    ];
  };

  renderCompareCell = (info: CellContext<T, any>) => {
    const { displayedColumns, evaluatingPortfolioForComparison, onCompare } = this.props;
    const data = info.row.original;
    const showCompareButton = displayedColumns.includes(PORTFOLIO_TABLE_COLUMNS.COMPARE);

    if (!showCompareButton || !onCompare || this.isUnallocatedPortfolio(data)) {
      return <></>;
    }

    const isComparing = this.isComparingIndividualPortfolio(data);
    const shouldDisableForEmptyHoldings = this.shouldDisableForEmptyHoldings(data);
    const isDisabled = evaluatingPortfolioForComparison || shouldDisableForEmptyHoldings;

    const button = (
      <PrimaryButton
        className="primary-v2 btn-compare"
        loading={isComparing}
        onClick={() => {
          this.setState({ isComparingMultiplePortfolios: false });
          onCompare([data]);
        }}
        compact
        disabled={isDisabled}
      >
        <FormattedMessage id={`PortfolioCard.${isComparing ? 'Comparing' : 'Compare'}`} />
      </PrimaryButton>
    );

    if (shouldDisableForEmptyHoldings) {
      return (
        <Tooltip className="custom-tooltip-v2" message={formatMessage('PortfolioCard.CompareDisabledEmptyHoldings')}>
          <span className="disabled-tooltip-wrapper">{button}</span>
        </Tooltip>
      );
    }

    return button;
  };

  renderMenuToggle = (info: CellContext<T, any>) => {
    const data = info.row.original;
    const menuActions = this.getMenuActions();

    const preparedMenuItems = prepareMenuItems(menuActions, data);

    if (!preparedMenuItems.length) {
      return <></>;
    }

    if (this.shouldShowMenuLoader(data, menuActions)) {
      return <LoadingSpinner />;
    }

    const isDisabled = this.props.evaluatingPortfolioForComparison || this.shouldDisableForEmptyHoldings(data);

    return <TableMenu menuItems={preparedMenuItems} disabled={isDisabled} />;
  };

  renderIdCell = (info: CellContext<T, any>) => {
    const data = info.row.original;
    const value = data._id;

    if (!value) {
      return <></>;
    }

    const idToDisplay = decodeIdForExistingClientPortfolio(value);

    return <MaskedData text={idToDisplay} charactersToShow={4} showTooltip />;
  };

  getTableColumns = (): ColumnDef<T>[] => {
    const { displayedColumns, locked } = this.props;

    const columns: ColumnDef<T>[] = [
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.NAME,
        cell: this.renderNameCell,
        header: formatMessage('PortfoliosTable.Headers.Name'),
        id: PORTFOLIO_TABLE_COLUMNS.NAME,
        meta: {
          serverSideSortKey: PORTFOLIO_TABLE_COLUMNS_KEYS.NAME,
        },
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.CONTACTS,
        cell: this.renderContactsCell,
        header: 'Contacts',
        id: PORTFOLIO_TABLE_COLUMNS.CONTACTS,
        meta: {
          serverSideSortKey: PORTFOLIO_TABLE_COLUMNS_KEYS.CONTACTS,
        },
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.WARNING,
        cell: this.renderWarningCell,
        enableSorting: false,
        header: '',
        id: PORTFOLIO_TABLE_COLUMNS.WARNING,
        size: 28,
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.LOCK,
        cell: this.renderLockCell,
        enableSorting: false,
        header: '',
        id: PORTFOLIO_TABLE_COLUMNS.LOCK,
        size: 28,
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.ID,
        cell: this.renderIdCell,
        header: formatMessage('PortfoliosTable.Headers.Id'),
        id: PORTFOLIO_TABLE_COLUMNS.ID,
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.ENTERPRISE_GROUP,
        cell: this.renderGroupCell,
        enableSorting: true,
        header: formatMessage('PortfoliosTable.Headers.Group'),
        id: PORTFOLIO_TABLE_COLUMNS.GROUP,
        meta: {
          serverSideSortKey: PORTFOLIO_TABLE_COLUMNS_KEYS.ENTERPRISE_GROUP,
        },
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.PLAN_ACCOUNT,
        cell: this.renderPlanAccountCell,
        header: formatMessage('PortfoliosTable.Headers.PlanAccount'),
        id: PORTFOLIO_TABLE_COLUMNS.PLAN_ACCOUNT,
        size: 200,
        meta: {
          serverSideSortKey: PORTFOLIO_TABLE_COLUMNS_KEYS.PLAN_ACCOUNT,
        },
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.RISK_PROFILE,
        cell: this.renderRiskProfileCell,
        header: formatMessage('PortfoliosTable.Headers.RiskProfile'),
        id: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
        size: 200,
        meta: {
          serverSideSortKey: PORTFOLIO_TABLE_COLUMNS_KEYS.RISK_PROFILE,
        },
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.SAVED_AT,
        cell: this.renderLastModifiedDataCell,
        header: formatMessage('PortfoliosTable.Headers.LastModified'),
        id: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
        size: 240,
        meta: {
          serverSideSortKey: PORTFOLIO_TABLE_COLUMNS_KEYS.SAVED_AT,
        },
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.ACTION_COMPARE,
        cell: this.renderCompareCell,
        enableSorting: false,
        header: '',
        id: PORTFOLIO_TABLE_COLUMNS.COMPARE,
        size: 140,
      },
      {
        accessorKey: PORTFOLIO_TABLE_COLUMNS_KEYS.ACTION_MENU_TOGGLE,
        cell: this.renderMenuToggle,
        enableSorting: false,
        header: '',
        id: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
        size: 64,
      },
    ];

    return columns.filter(
      column =>
        // Only use columns passed as props as well as the Warning if warningDetails is defined.
        displayedColumns.includes(column.id as PORTFOLIO_TABLE_COLUMNS) ||
        (locked && column.id === PORTFOLIO_TABLE_COLUMNS.LOCK) ||
        column.id === PORTFOLIO_TABLE_COLUMNS.WARNING,
    );
  };

  getEmptyDataComponent = (): JSX.Element => {
    const { query } = this.props;
    const i18nTypeEmptyTable = !query ? 'NoData' : 'NoResults';
    const i18nEmptyTablePath = `${this.i18nBasePath}.EmptyData.${i18nTypeEmptyTable}`;

    return <EmptySearchResults searchQuery={query} titleToLocalize={`${i18nEmptyTablePath}.Title`} messageToLocalize={`${i18nEmptyTablePath}.Message`} />;
  };

  isRowSelectionDisabled = (row: Row<T>) => {
    if (this.isUnallocatedPortfolio(row.original)) {
      return true;
    }

    // We allow forceSelectable when loading portfolios to comparison
    // But if the portfolios is unallocated, we still disable the selection to avoid adding it to comparison
    if (this.props.forceSelectable) {
      return false;
    }

    return this.props.missingProductPredicate?.(row.original) || this.shouldDisableForEmptyHoldings(row.original);
  };

  canRowExpand = (row: Row<T>) => {
    const { missingProductPredicate } = this.props;
    if (this.isUnallocatedPortfolio(row.original)) {
      return false;
    }

    if (this.shouldDisableForEmptyHoldings(row.original)) {
      return false;
    }

    return !missingProductPredicate || !missingProductPredicate(row.original as T);
  };

  isUnallocatedPortfolio = (portfolio: T): boolean => !!portfolio.flags?.includes(PortfolioFlag.UnallocatedPortfolio);

  shouldDisableForEmptyHoldings = (portfolio: T): boolean => {
    const { user } = this.props;
    const portfolioHasEmptyHoldings = hasEmptyHoldings(portfolio as unknown as Portfolio);

    if (!portfolioHasEmptyHoldings) {
      return false;
    }

    // If user has the permission to allow empty holdings in list, don't disable
    const hasEmptyHoldingsPermission = userHasPermission(user, PERMISSIONS.PORTFOLIOS_ALLOW_EMPTY_HOLDINGS_IN_LIST);
    return !hasEmptyHoldingsPermission;
  };

  getRowAriaLabelledBy = (row: Row<T>) => `portfolio-name-${row.original._id}`;

  render() {
    const {
      className,
      evaluatingPortfolioForComparison,
      displayedColumns,
      serverSideSorting,
      portfolios,
      selectedTitle,
      onRowSelectionChange,
      onServerSideSorting,
      sortedColumns,
    } = this.props;
    const showDetails = displayedColumns.includes(PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS);

    return (
      <div className={classNames('portfolios-table-v2', className)}>
        {portfolios && (
          <DataTableV2
            canRowExpand={row => this.canRowExpand(row as unknown as Row<T>)}
            columns={this.state.columns}
            data={portfolios}
            serverSideSorting={serverSideSorting}
            enableMultiselect
            enableSorting
            enableTableSelection
            isExpandable={showDetails}
            infiniteScrollOptions={{
              show: this.props.hasMore ?? false,
              onEnter: this.props.onLoadMore,
            }}
            multiselectActionsOptions={{
              actions: this.getMultiselectActions() as MultiselectAction<object>[],
              selectedTitle,
            }}
            noDataComponent={this.getEmptyDataComponent}
            onServerSideSorting={sort => onServerSideSorting && onServerSideSorting(sort as ColumnSort)}
            onRowSelectionChange={onRowSelectionChange as ((selectedRows: object[]) => void) | undefined}
            renderExpandedContent={this.getExpandedContent}
            rowSelectionDisabled={(row: Row<T>) => evaluatingPortfolioForComparison || this.isRowSelectionDisabled(row)}
            sortedColumns={sortedColumns}
            getRowAriaLabelledBy={(row: Row<T>) => this.getRowAriaLabelledBy(row)}
            getAdditionalRowClassNames={this.getRowClassNames}
            tableClassNames={{
              isBodyHoverActive: true,
            }}
            disabledActions={evaluatingPortfolioForComparison}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  evaluatingPortfolioForComparison: selectIsLoadingSome(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]),
  user: state.auth.user || null,
});

const mapDispatchToProps = {
  removePlanAccountFromPortfolio,
};

export default connect(mapStateToProps, mapDispatchToProps)(PortfoliosTableV2 as unknown as React.ComponentType) as <T>(
  props: ComponentProps<T>,
) => JSX.Element;
